import { formatCell } from "./formatCell"
import {
  peerCellOf,
  sigElementIndexAt,
  sigLevelPercent,
  sigSourceOf,
  SigLevelName,
} from "./sigCells"
import type {
  CellSig,
  NumberFormatSpec,
  SigMeta,
  StatKind,
  TableModel,
} from "./tableModel.types"

type NumberFormats = Partial<Record<StatKind, NumberFormatSpec>>

const LEVEL_ORDER: SigLevelName[] = ["upper", "lower"]

/**
 * Display name of a banner element: its own label ("Male"), or the whole
 * path ("Q1 / Yes") when another element has the same label.
 */
export const sigElementName = (meta: SigMeta, index: number): string => {
  const header = meta.elements[index]?.header ?? []
  const label = header[header.length - 1] ?? ""
  const shared = meta.elements.some(
    (element, other) =>
      other !== index && element.header[element.header.length - 1] === label
  )
  return shared && header.length > 1 ? header.join(" / ") : label
}

/** "A", "A and B", "A, B and C". */
const listOf = (items: string[]): string =>
  items.length <= 1
    ? (items[0] ?? "")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`

/** "P1", "P1, and P2", "P1, P2, and P3". */
const joinParts = (parts: string[]): string =>
  parts.length <= 1
    ? (parts[0] ?? "")
    : `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`

const levelText = (meta: SigMeta, level: SigLevelName): string => {
  const percent = sigLevelPercent(meta, level)
  return percent === undefined ? "" : ` at ${percent}%`
}

/** Plain-English reason a cell was not tested. */
const skipText = (sig: CellSig, meta: SigMeta): string => {
  if (sig.lowBase || sig.skip === "low_base") {
    return `Not tested: base below ${meta.minBase}.`
  }
  switch (sig.skip) {
    case "extreme":
      return "Not tested: 0% and 100% cannot be tested."
    case "too_small":
      return "Not tested: too few respondents."
    case "no_variance":
      return "Not tested: no variation to test."
    default:
      return "Not tested."
  }
}

/** Plain-English reason the comparison with the rest of the sample was skipped. */
const REST_SKIP_REASONS: Record<string, string> = {
  extreme: "0% or 100%",
  low_base: "the rest of the sample is below the minimum base",
  too_small: "too few respondents",
  no_variance: "no variation",
}

/** "It could not be compared with the rest of the sample (0% or 100%)." */
const restSkipSentence = (restSkip: string): string => {
  const reason = Object.hasOwn(REST_SKIP_REASONS, restSkip)
    ? REST_SKIP_REASONS[restSkip]
    : undefined
  return `It could not be compared with the rest of the sample${reason ? ` (${reason})` : ""}.`
}

/**
 * Plain-English explanation of a cell's significance result, for the hover
 * tooltip. `y`, `x` are engine data-array coordinates (`data[y][x]`), the
 * `data-y` / `data-x` of the rendered cell.
 *
 * - A tested cell lists what it is higher than (its letters), what it is
 *   lower than (other cells whose letters name it) and the rest-of-sample
 *   result: "Male (61.0%) is higher than Female (44.0%) at 95%, and higher
 *   than the rest of the sample at 95%."
 * - A tested cell without a result says it is not significantly different
 *   from the columns it could be compared with only when another column of
 *   its group was tested too (some of its pairs may still have been
 *   skipped); otherwise "Not compared: no other column in its group could be
 *   tested."
 * - With REST: when the engine says the rest-of-sample comparison was skipped
 *   (`restSkip`), it says so with the reason ("It could not be compared with
 *   the rest of the sample (0% or 100%)."); only when that comparison ran
 *   without a result does it say no difference from the rest was found.
 * - A skipped cell gives the reason: "Not tested: base below 30."
 * - A `sig` cell (letters shown as a row) explains the cell it belongs to.
 * - Any other cell, or a model without significance, gives null.
 *
 * @param numberFormats per-kind formats the table shows values with (the
 *   style's formats when they override the engine's)
 * @param decimals the style's decimals per kind, replacing the decimals of
 *   the format a value is shown with
 */
export const describeCellSig = (
  model: TableModel,
  y: number,
  x: number,
  numberFormats?: NumberFormats,
  decimals?: Partial<Record<StatKind, number>>
): string | null => {
  const meta = model.significance
  if (!meta) return null
  let row = y - model.freeze.y
  let col = x - model.freeze.x
  let cell = model.cells[row]?.[col]
  if (!cell) return null

  if (!cell.sig && cell.role === "letters") {
    const source = sigSourceOf(model, row, col)
    if (!source) return null
    row = source.row
    col = source.col
    cell = model.cells[row][col]
  }
  const sig = cell.sig
  if (!sig) return null
  if (!sig.tested) return skipText(sig, meta)

  const own = sigElementIndexAt(model, row, col)
  if (own < 0) return null
  const valueOf = (r: number, c: number): string =>
    formatCell(model.cells[r][c], numberFormats, decimals)
  const named = (element: number): string => {
    const peer = peerCellOf(model, row, col, element)
    const name = sigElementName(meta, element)
    return peer ? `${name} (${valueOf(peer.row, peer.col)})` : name
  }

  const parts: string[] = []

  // Higher: this cell's own letters, grouped by level.
  LEVEL_ORDER.forEach((level) => {
    const names = sig.letters
      .filter((letter) => letter.level === level)
      .map((letter) => named(letter.element))
    if (names.length > 0) {
      parts.push(`higher than ${listOf(names)}${levelText(meta, level)}`)
    }
  })

  // Lower: other cells of the same line whose letters name this element.
  const lowerThan: Record<SigLevelName, string[]> = { upper: [], lower: [] }
  meta.elements.forEach((_, element) => {
    if (element === own) return
    const peer = peerCellOf(model, row, col, element)
    const letter = peer
      ? model.cells[peer.row][peer.col].sig?.letters.find(
          (candidate) => candidate.element === own
        )
      : undefined
    if (letter) lowerThan[letter.level].push(named(element))
  })
  LEVEL_ORDER.forEach((level) => {
    if (lowerThan[level].length > 0) {
      parts.push(
        `lower than ${listOf(lowerThan[level])}${levelText(meta, level)}`
      )
    }
  })

  if (sig.vsRest) {
    const direction = sig.vsRest.dir === "up" ? "higher" : "lower"
    parts.push(
      `${direction} than the rest of the sample${levelText(meta, sig.vsRest.level)}`
    )
  }

  const subject = `${sigElementName(meta, own)} (${valueOf(row, col)})`
  // The rest-of-sample test was skipped: say so, whatever else is said.
  const restSkipped =
    meta.rest && !sig.vsRest && sig.restSkip
      ? ` ${restSkipSentence(sig.restSkip)}`
      : ""
  if (parts.length > 0) return `${subject} is ${joinParts(parts)}.${restSkipped}`

  // Nothing significant. Claim "not different" only when a comparison could
  // be made: another element of the group has a tested cell on this line.
  // (Every pair may have been skipped, e.g. the only other column is 0%.)
  const group = meta.elements[own]?.group ?? null
  const compared =
    group !== null &&
    meta.elements.some((element, index) => {
      if (index === own || element.group !== group || element.lowBase) {
        return false
      }
      const peer = peerCellOf(model, row, col, index)
      const peerSig = peer ? model.cells[peer.row][peer.col].sig : undefined
      return peerSig?.tested === true && !peerSig.lowBase
    })
  const line = meta.bannerAxis === "across" ? "column" : "row"
  // "No difference from the rest" only when that comparison ran (it has no
  // arrow here, so it was not significant); a skipped one says why instead.
  const rest =
    meta.rest && !restSkipped
      ? "no significant difference from the rest of the sample was found"
      : ""
  if (compared) {
    return `${subject} is not significantly different from the ${line}s it could be compared with${rest ? `, and ${rest}` : ""}.${restSkipped}`
  }
  const restSentence = rest
    ? ` ${rest.charAt(0).toUpperCase()}${rest.slice(1)}.`
    : restSkipped
  return `Not compared: no other ${line} in its group could be tested.${restSentence}`
}

/**
 * Screen-reader text for a cell's letters, in banner order:
 * "higher than South (D) at 95%, higher than West (f) at 90%".
 */
export const describeSigLetters = (meta: SigMeta, sig: CellSig): string =>
  sig.letters
    .map(
      (letter) =>
        `higher than ${sigElementName(meta, letter.element)} (${letter.letter})${levelText(meta, letter.level)}`
    )
    .join(", ")

/** Screen-reader text for a vs-rest arrow: "higher than the rest of the sample at 95%". */
export const describeSigArrow = (
  meta: SigMeta,
  vsRest: NonNullable<CellSig["vsRest"]>
): string =>
  `${vsRest.dir === "up" ? "higher" : "lower"} than the rest of the sample${levelText(meta, vsRest.level)}`

/**
 * Plain-English note for a banner element's letter cell ("(A)"): its base
 * and, when low base, why it is not tested.
 */
export const describeSigElement = (
  model: TableModel,
  index: number
): string | null => {
  const meta = model.significance
  const element = meta?.elements[index]
  if (!meta || !element || element.header.length === 0) return null
  const name = sigElementName(meta, index)
  const letter = element.letter ? ` (${element.letter})` : ""
  const effective =
    element.baseEff !== undefined
      ? `, effective base ${Math.round(element.baseEff * 10) / 10}`
      : ""
  const base = `${name}${letter}: base ${element.baseN}${effective}.`
  return element.lowBase
    ? `${base} Not tested: base below ${meta.minBase}.`
    : base
}
